/* eslint-disable import/no-unresolved */
import React, { useCallback, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { withRouter } from "react-router-dom";
import { push } from "react-router-redux";
import Yup from "yup";
import { bool, func, shape, string } from "prop-types";

import { LoginSelectEnvironment } from "@ui-kit/components/index.es";
import { actions as layoutActions } from "reducers/layout";
import { actions as loginActions, selectors as loginSelectors } from "reducers/login";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as configSelectors } from "reducers/config";
import * as i18n from "util/i18n";

import withExchangeToken from "pages/_components/withExchangeToken";
import { resizableRoute } from "pages/_components/Resizable";
import { getPostLoginHomePath, getUnauthenticatedLoginPath } from "util/v3UxConfig";
import { isUxDashboardImprovementsEnabled } from "util/v3ShellLayoutSync";

import { LOGIN_SELECT_ENV_LABELS_KEYS } from "./loginI18nKeys";

function mapEnvironmentsToOptions(environmentsEntries) {
    if (!environmentsEntries || !Array.isArray(environmentsEntries)) {
        return [];
    }
    return environmentsEntries
        .sort((a, b) => (a[1]?.name || "").localeCompare(b[1]?.name || ""))
        .map(([id, env]) => ({
            id: String(id),
            variant: env.environmentType === "retail" ? "person" : "corporate",
            children: env.name || id,
            bloqued: env.allowedToAccess?.toString() === "false",
        }));
}

function LoginStep3V3({
    dispatch,
    userFirstName,
    username,
    activeEnvironment,
    environments,
    isSubmitting,
    fetchingBiometric,
    uxLoginImprovements,
    uxDashboardImprovements,
    location,
}) {
    useEffect(() => {
        dispatch(layoutActions.setScopes(["loginStep3", "loginStep4"]));
        return () => {
            dispatch(layoutActions.setScopes([]));
            dispatch(loginActions.resetLoading());
        };
    }, [dispatch]);

    useEffect(() => {
        const pathname = location?.pathname || "";
        const onStep3Path =
            pathname === "/v3/login/step3" ||
            pathname === "/v3/login/step3/" ||
            pathname === "/loginStep3" ||
            pathname === "/loginStep3/";
        const hasEnvironments = Boolean(environments && Object.keys(environments).length > 0);

        // Only auto-navigate from step 3; env/environments updates otherwise re-dispatch push() → max update depth.
        if (activeEnvironment && onStep3Path) {
            dispatch(push(getPostLoginHomePath(uxDashboardImprovements)));
        } else if (!username && !fetchingBiometric && !isSubmitting && !hasEnvironments) {
            dispatch(push(getUnauthenticatedLoginPath(uxLoginImprovements)));
        }
    }, [
        dispatch,
        username,
        activeEnvironment,
        fetchingBiometric,
        isSubmitting,
        environments,
        uxLoginImprovements,
        uxDashboardImprovements,
        location?.pathname,
    ]);

    const environmentsOptions = useMemo(() => mapEnvironmentsToOptions(Object.entries(environments || {})), [
        environments,
    ]);

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                environment: Yup.string().required(i18n.get("login.step3.environment.required")),
            }),
        [],
    );

    const handleSubmit = useCallback(
        (values, formikBag) => {
            dispatch(loginActions.loginStep3(values.environment, values.rememberEnvironment, formikBag));
        },
        [dispatch],
    );

    const getLabels = useCallback((key) => i18n.get(LOGIN_SELECT_ENV_LABELS_KEYS[key] ?? key), []);
    const welcomeTitle = useMemo(() => {
        const firstName = userFirstName || "";
        const formatted =
            firstName && firstName.length > 1
                ? firstName
                      .toLowerCase()
                      .split(" ")
                      .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
                      .join(" ")
                : "";
        return i18n.get("login.step3.header").replace("#{USER_NAME}", formatted);
    }, [userFirstName]);

    return (
        <LoginSelectEnvironment
            environments={environmentsOptions}
            getLabels={getLabels}
            welcomeTitle={welcomeTitle}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
            submitLoading={isSubmitting}
        />
    );
}

LoginStep3V3.propTypes = {
    dispatch: func.isRequired,
    userFirstName: string.isRequired,
    username: string.isRequired,
    activeEnvironment: shape({}),
    environments: shape({}).isRequired,
    isSubmitting: bool.isRequired,
    fetchingBiometric: bool.isRequired,
    uxLoginImprovements: bool,
    uxDashboardImprovements: bool,
    location: shape({ pathname: string }).isRequired,
};

LoginStep3V3.defaultProps = {
    activeEnvironment: null,
    uxLoginImprovements: false,
    uxDashboardImprovements: false,
};

const mapStateToProps = (state) => ({
    userFirstName: loginSelectors.getUserFirstName(state),
    username: loginSelectors.getUsername(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    environments: loginSelectors.getEnvironments(state),
    isSubmitting: loginSelectors.getFetching(state),
    fetchingBiometric: loginSelectors.getFetchingBiometric(state),
    uxLoginImprovements: !!configSelectors.getConfigKey(state, "frontend.UXImprovements.Login.functionalities"),
    uxDashboardImprovements: isUxDashboardImprovementsEnabled(),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute, withExchangeToken)(LoginStep3V3);
